<script setup>
import { ref } from 'vue'
import RiCloseLine from 'vue-remix-icons/icons/ri-close-line.vue'

const inputRef = ref()

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  size: { type: String, default: 'md', validator: (s) => ['md', 'lg'].includes(s) },
  autofocus: {
    type: Boolean,
    default: false,
  },
  disabled: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  success: { type: Boolean, default: false },
  type: { type: String, default: 'text' },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: null },
  icon: { type: [String, null], default: null }, // See vue-remix-icons.js
  iconPosition: {
    type: String,
    default: 'left',
    validator: (i) => ['left', 'right'].includes(i),
  },
  name: { type: String, default: null },
  inputClass: { type: String, default: '' },
  min: { type: String, default: null },
  max: { type: String, default: null },
  variant: {
    type: String,
    default: 'default',
    validator: (i) => ['white', 'default'].includes(i),
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  ariaLabel: { type: String, default: null },
  ariaLabelledby: { type: String, default: null },
  ariaDescribedby: { type: String, default: null },
  ariaErrormessage: { type: String, default: null },
  inputmode: { type: String, default: null },
  maxlength: { type: Number, default: null },
})
const emit = defineEmits(['update:modelValue', 'blur', 'keypress'])

function onKeypressSpace(event) {
  if (props.type === 'email') {
    event.preventDefault()
  }
}

defineExpose({
  focus: () => inputRef.value?.focus(),
})
</script>

<template>
  <div class="w-full relative">
    <component
      :is="icon"
      v-if="icon && iconPosition === 'left'"
      class="absolute inset-y-0 left-4 fill-current flex-none my-auto w-4 h-4 pointer-events-none"
      aria-hidden="true"
    />

    <input
      :id="name"
      ref="inputRef"
      :value="modelValue"
      :type="type"
      :inputmode="inputmode"
      :required="required"
      :placeholder="placeholder"
      :disabled="disabled"
      :min="min"
      :max="max"
      :aria-label="ariaLabel"
      :aria-labelledby="ariaLabelledby"
      :aria-describedby="ariaDescribedby"
      :aria-invalid="error"
      :aria-errormessage="ariaErrormessage"
      :maxlength="maxlength"
      :autofocus="autofocus"
      :class="[
        'border-none rounded-t w-full h-full',
        {
          'bg-[#EEEEEE] shadow-[inset_0_-2px_0_0_#3A3A3A] focus:!shadow-[inset_0_-2px_0_0_#3A3A3A]':
            variant === 'default',
        },
        {
          'bg-white shadow-[inset_0_-2px_0_0_#000091] focus:!shadow-[inset_0_-2px_0_0_#000091]':
            variant === 'white',
        },
        { ' shadow-[inset_0_-2px_0_0_#3A3A3A]': !disabled },
        { 'cursor-not-allowed shadow-[inset_0_-2px_0_0_#E5E5E5]': disabled },
        { '!shadow-[inset_0_-2px_0_0_#ce0500]': error },
        { '!shadow-[inset_0_-2px_0_0_#18753c]': success },
        { 'py-3': size === 'lg' },
        {
          'pl-12': icon && iconPosition === 'left' && ['md', 'lg'].includes(size),
        },
        {
          'pr-12': icon && iconPosition === 'right' && ['md', 'lg'].includes(size),
        },
        inputClass,
      ]"
      @keypress.space="onKeypressSpace"
      @keypress="emit('keypress', $event)"
      @input="emit('update:modelValue', $event.target.value)"
      @blur="emit('blur')"
    />

    <RiCloseLine
      v-if="clearable && modelValue"
      class="absolute inset-y-0 right-4 fill-current flex-none my-auto w-4 h-4"
      @click="emit('update:modelValue', null)"
    />
    <component
      :is="icon"
      v-if="icon && iconPosition === 'right' && !modelValue"
      class="absolute inset-y-0 right-4 fill-current flex-none my-auto w-4 h-4 pointer-events-none"
      aria-hidden="true"
    />
  </div>
</template>

<!-- <script>
export default defineNuxtComponent({
  props: {
    modelValue: { type: [String, Number], default: null },
    size: { type: String, default: 'md', validator: (s) => ['md', 'lg'].includes(s) },
    autofocus: {
      type: Boolean,
      default: false,
    },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    success: { type: Boolean, default: false },
    type: { type: String, default: 'text' },
    required: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    icon: { type: [String, null], default: null }, // See vue-remix-icons.js
    iconPosition: {
      type: String,
      default: 'left',
      validator: (i) => ['left', 'right'].includes(i),
    },
    name: { type: String, default: null },
    inputClass: { type: String, default: '' },
    min: { type: String, default: null },
    max: { type: String, default: null },
    variant: {
      type: String,
      default: 'default',
      validator: (i) => ['white', 'default'].includes(i),
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    ariaLabel: { type: String, default: null },
    ariaLabelledby: { type: String, default: null },
    ariaDescribedby: { type: String, default: null },
    ariaErrormessage: { type: String, default: null },
    inputmode: { type: String, default: null },
    maxlength: { type: Number, default: null },
  },
  emits: ['update:modelValue', 'blur', 'keypress'],
  methods: {
    onKeypressSpace(event) {
      if (this.type === 'email') {
        event.preventDefault()
      }
    },
  },
})
</script> -->

<style lang="postcss" scoped>
input {
  --tw-ring-shadow: 0 0 #000 !important;
}

/* @todo */
/* input[type="search"]::-webkit-search-cancel-button {

} */
</style>
